/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: SearchBar.tsx
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  11.11.22 5:38 pm.
 * Last modified: 11.11.22 5:38 pm.
 *
 * More information at: https://egt-digital.com/
 */
import React, { ChangeEvent, useEffect, useState } from 'react';
import { setSearchBy } from '../api';
import { addBuildHash } from '../api/helpers';
import { useSettings } from '../contexts/SettingsContext';
import { useTranslator } from '../contexts/TranslatorContext';
import store from '../eventStore';

const SearchBarDesktop = ({ host, onChange, value, onClickClose, openType }: ISearchBarSubComponentProps) => {
    const translate = useTranslator();

    return (
        <div className={`egt_search_bar egt_search_bar_desktop egt_search_bar_${openType}`}>
            <div className="egt_search_components">
                <input
                    type="search"
                    autoComplete="off"
                    placeholder={translate('searchPlaceHolder')}
                    className="egt_search_component_input"
                    onChange={onChange}
                    value={value}
                />
                <img
                    className="egt_search_component_back"
                    src={addBuildHash(`${host}/assets/portal/ui/backspace.svg`)}
                    onClick={() => setSearchBy('')}
                    alt=""
                />
                <img
                    onClick={onClickClose}
                    className="egt_search_component_close"
                    src={addBuildHash(`${host}/assets/portal/ui/close.svg`)}
                    alt=""
                />
            </div>
        </div>
    );
};

const SearchBarMobile = ({ host, onChange, value, onClickClose, openType }: ISearchBarSubComponentProps) => {
    const translate = useTranslator();

    return (
        <div className={`egt_search_bar egt_search_bar_mobile egt_search_bar_${openType}`}>
            <div className="egt_search_component_close_container">
                <img
                    onClick={onClickClose}
                    className="egt_search_component_close"
                    src={addBuildHash(`${host}/assets/portal/ui/${openType === 'ingame' ? 'back' : 'close'}.svg`)}
                    alt=""
                />
            </div>
            <input
                type="search"
                autoComplete="off"
                placeholder={translate('searchPlaceHolder')}
                className="egt_search_component_input"
                onChange={onChange}
                value={value}
            />
            <img
                className="egt_search_component_back"
                src={addBuildHash(`${host}/assets/portal/ui/backspace.svg`)}
                onClick={() => setSearchBy('')}
                alt=""
            />
        </div>
    );
};

interface ISearchBarSubComponentProps {
    host: string;
    value: string;
    openType: 'ingame' | 'fullscreen';
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onClickClose: (e: any) => void;
}

const SearchBar = ({ variant }: ISearchBarProps) => {
    const [searchStr, setSearchStr] = useState<string>(store.SEARCH.value.inputValue);
    const [settings, setSettings] = useSettings();

    useEffect(() => {
        const searchSubscription = store.SEARCH.subscribe(value => {
            setSearchStr(value.inputValue);
        });

        return () => searchSubscription.unsubscribe();
    }, []);

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchBy(e.target.value);
    };

    const handleOnClickClose = () => {
        setSettings?.({
            ...settings,
            isSearchOpen: false,
        });
    };

    return (
        <>
            {variant === 'desktop' && (
                <SearchBarDesktop
                    value={searchStr}
                    openType={settings.openType}
                    host={settings.host}
                    onChange={handleOnChange}
                    onClickClose={handleOnClickClose}
                />
            )}
            {variant === 'mobile' && (
                <SearchBarMobile
                    value={searchStr}
                    openType={settings.openType}
                    host={settings.host}
                    onChange={handleOnChange}
                    onClickClose={handleOnClickClose}
                />
            )}
        </>
    );
};

interface ISearchBarProps {
    variant?: 'mobile' | 'desktop';
}

SearchBar.defaultProps = {
    variant: 'mobile',
};

export default SearchBar;
